import { useEffect, useState } from "react";
import { Building2, FileText, IndianRupee, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg, fmtINR, fmtDateTime } from "../../lib/api";
import { PageHeader, StatCard, StatusBadge, LoadingRows, TableShell, TR, TD, EmptyState } from "../../components/Shared";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

export default function RootRealEstate() {
  const [ov, setOv] = useState(null);
  const [invs, setInvs] = useState(null);
  const [enqs, setEnqs] = useState(null);
  const [props, setProps] = useState(null);
  const [st, setSt] = useState("");

  const loadInvs = (s = st) => api.get("/root/re/invoices", { params: s ? { status: s } : {} }).then((r) => setInvs(r.data.invoices)).catch(() => setInvs([]));
  useEffect(() => {
    api.get("/root/re/overview").then((r) => setOv(r.data)).catch((e) => toast.error(errMsg(e)));
    loadInvs("");
    api.get("/root/re/enquiries").then((r) => setEnqs(r.data.enquiries)).catch(() => setEnqs([]));
    api.get("/root/re/properties").then((r) => setProps(r.data.properties)).catch(() => setProps([]));
  }, []);

  return (
    <div className="space-y-8" data-testid="root-realestate">
      <PageHeader title="Real Estate" subtitle="Platform-wide properties, enquiries, invoices & billing" testid="root-re-title" />

      {!ov ? <LoadingRows rows={2} /> : (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
          <StatCard icon={Building2} label="Properties" value={ov.properties} testid="re-ov-props" tint="bg-blue-500/10 text-blue-500" />
          <StatCard icon={Building2} label="Published" value={ov.published} testid="re-ov-pub" delay={0.05} tint="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={FileText} label="Enquiries" value={ov.enquiries} testid="re-ov-enq" delay={0.1} tint="bg-amber-500/10 text-amber-500" />
          <StatCard icon={FileText} label="Invoices" value={ov.invoices} testid="re-ov-inv" delay={0.15} tint="bg-violet-500/10 text-violet-500" />
          <StatCard icon={IndianRupee} label="RE Revenue" value={fmtINR(ov.revenue)} testid="re-ov-rev" delay={0.2} tint="bg-emerald-500/10 text-emerald-500" />
          <StatCard icon={Users} label="Active Subscriptions" value={ov.subscriptions_active} testid="re-ov-subs" delay={0.25} />
        </div>
      )}

      <div>
        <div className="flex items-center gap-3 mb-3 flex-wrap">
          <h2 className="font-display font-bold text-lg">All Invoices</h2>
          <Select value={st || "all"} onValueChange={(v) => { const s = v === "all" ? "" : v; setSt(s); loadInvs(s); }}>
            <SelectTrigger data-testid="re-inv-filter" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>{["all", "paid", "pending", "partially_paid", "overdue", "cancelled", "refunded"].map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace("_", " ")}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {!invs ? <LoadingRows rows={3} /> : invs.length === 0 ? <EmptyState title="No invoices yet" /> : (
          <TableShell head={["Number", "Customer", "Total", "Paid", "Status", "Date", "PDF"]} testid="re-invoices-table">
            {invs.map((i) => (
              <TR key={i.id} testid={`re-inv-${i.id}`}>
                <TD><span className="font-bold text-sm">{i.number}</span><p className="text-[10px] text-muted-foreground">{i.source}</p></TD>
                <TD><span className="text-xs">{i.customer_name}</span></TD>
                <TD><span className="font-bold">{fmtINR(i.total)}</span></TD>
                <TD>{fmtINR(i.amount_paid)}</TD>
                <TD><StatusBadge status={i.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{i.invoice_date}</span></TD>
                <TD><a href={`/api/root/re/invoices/${i.id}/pdf`} target="_blank" rel="noreferrer" data-testid={`re-inv-pdf-${i.id}`}
                  className="text-xs font-bold text-primary hover:underline">View PDF</a></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">Recent Enquiries</h2>
        {!enqs ? <LoadingRows rows={3} /> : enqs.length === 0 ? <EmptyState title="No enquiries yet" /> : (
          <TableShell head={["Customer", "Property", "Type", "Status", "When"]} testid="re-enq-table">
            {enqs.slice(0, 50).map((e) => (
              <TR key={e.id} testid={`re-enq-${e.id}`}>
                <TD><p className="font-semibold text-sm">{e.name}</p><p className="text-[10px] text-muted-foreground">{e.phone}{e.email ? ` · ${e.email}` : ""}</p></TD>
                <TD><span className="text-xs">{e.property_name || "—"}</span></TD>
                <TD><span className="text-xs capitalize">{e.type}</span></TD>
                <TD><StatusBadge status={e.status} /></TD>
                <TD><span className="text-xs text-muted-foreground">{fmtDateTime(e.created_at)}</span></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>

      <div>
        <h2 className="font-display font-bold text-lg mb-3">All Properties</h2>
        {!props ? <LoadingRows rows={3} /> : props.length === 0 ? <EmptyState title="No properties yet" /> : (
          <TableShell head={["Property", "Category", "Location", "Price", "Status"]} testid="re-props-table">
            {props.slice(0, 100).map((p) => (
              <TR key={p.id} testid={`re-prop-${p.id}`}>
                <TD><p className="font-semibold text-sm max-w-[220px] truncate">{p.title}</p><p className="text-[10px] text-muted-foreground">{p.property_id}</p></TD>
                <TD><span className="text-xs">{p.category}</span></TD>
                <TD><span className="text-xs">{[p.location?.district, p.location?.state].filter(Boolean).join(", ") || "—"}</span></TD>
                <TD><span className="font-bold">{fmtINR(p.price)}</span></TD>
                <TD><StatusBadge status={p.published ? p.status.toLowerCase().replace(/\s+/g, "_") : "draft"} /></TD>
              </TR>
            ))}
          </TableShell>
        )}
      </div>
    </div>
  );
}
